/**
 * Fetches Rovo Insights data through the Rovo Insights service and maps it for the feed UI.
 */
import { useMemo } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { InsightsFeedResponse as ApiInsightResponse } from '@atlassian/rovo-insights-service-api/types';
import { RovoInsightsService } from '@atlassian/rovo-insights-service/service';

import { mapApiResponseToInsightItems } from './mapApiResponseToInsightItems';
import type { UseInsightsFeedResult } from './UseInsightsFeedResult';

interface UseInsightsFeedProps extends AssistanceServiceInjectedParams {
	/** When true, request debug_info from the API. */
	debugInfo?: boolean;
	/** When false, the fetch is not triggered. Defaults to true. */
	enabled?: boolean;
}

export const useInsightsFeed: (
	props: Omit<UseInsightsFeedProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseInsightsFeedResult = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
		debugInfo: debugInfoRequested,
		enabled,
	}: UseInsightsFeedProps) => {
		const insightsService = useMemo(() => {
			const config = assistanceService.getConfig();
			return new RovoInsightsService(config);
		}, [assistanceService]);

		const { data, error, isFetching, refetch } = useQuery<ApiInsightResponse>({
			queryKey: ['insightsFeed', String(debugInfoRequested ?? false)],
			enabled,
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.FETCH_INSIGHTS_FEED,
					name: ExperienceName.FETCH_INSIGHTS_FEED,
				});
				try {
					const response = await insightsService.getInsightsFeed({
						debugInfo: debugInfoRequested,
					});
					experienceTracker.succeed({
						name: ExperienceName.FETCH_INSIGHTS_FEED,
					});
					return response;
				} catch (error) {
					experienceTracker.fail({
						name: ExperienceName.FETCH_INSIGHTS_FEED,
						error: error instanceof Error ? error : new Error(String(error)),
					});
					throw error;
				}
			},
		});

		const mapped = useMemo(() => (data ? mapApiResponseToInsightItems(data) : null), [data]);
		const hasDebugData = mapped?.items.some((item) => item.debugInfo !== undefined) ?? false;

		return {
			items: mapped?.items ?? [],
			totalCount: mapped?.totalCount ?? 0,
			summary: mapped?.summary ?? '',
			summaryDetails: mapped?.summaryDetails,
			generatedAt: mapped?.generatedAt,
			isLoading: isFetching && !data,
			hasError: !isFetching && !data && error !== null,
			retry: refetch,
			hasDebugData,
			rawResponse: data ?? null,
		};
	},
);
